import { typesById } from '@modules/products/product-types';
import SessionCache from '@plugins/cache';
import { defineStore } from 'pinia';
import { useServiceStore } from '@modules/service/m-service';
import { useProductsStore } from '@modules/products/m-products';

const cache = new SessionCache('resources');

const hashCode = (str) =>
	str.split('').reduce((red, value) => ((red << 5) - red + value.charCodeAt(0)) | 0, 0);

const resolveList = (data) =>
	data?.invoiceList ??
	data?.batchList ??
	data?.customerList ??
	data?.financingList ??
	data?.invoicesList ??
	(Array.isArray(data) ? data : []);

export const useResourcesStore = defineStore('m-resources', {
	state: () => ({
		hasResult: true,
	}),

	actions: {
		setHasResult(value) {
			this.hasResult = value;
		},
		fetch({ resource, productId, paginationKey, force, query = {} }) {
			const id = `${productId}/${resource}`;
			const url = `/products/${id}`;
			const method = 'GET';
			const key = hashCode(resource.concat(productId, Object.values(query)));
			const cacheKey = `resourcesList/${id}/${key}`;
			const queryParams = {};
			if (cache.has(cacheKey) && !paginationKey) {
				const data = cache.get(cacheKey);
				this.setHasResult(data?.data?.length > 0);
				return Promise.resolve(data);
			}

			if (paginationKey) {
				Object.assign(queryParams, { paginationKey });
			}

			if (force) {
				Object.assign(queryParams, { generateOtp: true });
			}

			if (query) {
				Object.assign(queryParams, query);
			}

			const serviceStore = useServiceStore();
			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams,
				})
				.then(({ data }) => {
					const list = resolveList(data.data);
					const response = {
						reference: data.reference,
						paging: data.paging,
						data: list.map((item, index) => {
							const itemId = item.id ?? index;
							Object.assign(item, { reference: data.reference, id: itemId });
							cache.set(`resource/${id}/${itemId}`, item);

							return item;
						}),
					};

					if (cache.has(cacheKey)) {
						const result = cache.get(cacheKey);
						Object.assign(response.data, result.data.concat(response.data));
					}

					this.setHasResult(list.length > 0);

					cache.set(cacheKey, response);
					return response;
				});
		},

		async get({ resource, productId, resourceId }) {
			const productsStore = useProductsStore();
			const productSplit = productId.split('/')[0];
			let product;
			let type;
			let isLoan;
			// Try catch in case products are not cached yet. Could have been added manualy like cardDeferral movement
			try {
				product = await productsStore.get(productSplit);
				type = typesById[product.productType?.id];
				isLoan = type === 'loan';
			} catch {
				/* empty */
			}

			const id = `${productId}/${resource}/${resourceId}`;
			const url = `/products/${id}`;
			const method = 'GET';
			if (isLoan) {
				const key = `resource/${id}/detail`;

				if (cache.has(key)) {
					return Promise.resolve(cache.get(key));
				}

				return new Promise((resolve, reject) => {
					const serviceStore = useServiceStore();

					return serviceStore
						.request({
							service: {
								request: {
									url,
									method,
								},
							},
						})
						.then(({ data }) => {
							cache.set(key, data);
							resolve(data);
						})
						.catch((error) => reject(error));
				});
			}
			return cache.get(`resource/${id}`);
		},

		addCachedResource({ productId, resource = 'movements', resourceId, data }) {
			const key = `resource/${productId}/${resource}/${resourceId}`;

			cache.set(key, data);
		},

		updateCachedResource({ productId, resource = 'movements', resourceId, data }) {
			const key = `resource/${productId}/${resource}/${resourceId}`;
			const cacheResource = cache.get(key);
			const cacheIterator = cache.store;
			const comment = {
				comment: data.comment,
				commentId: data.commentId,
			};

			// We have to update the resources list as well
			cacheIterator.forEach((cacheValue, cacheKey) => {
				if (
					cacheKey?.startsWith(`resourcesList/${productId}/${resource}`) &&
					!cacheKey?.includes('withholdings')
				) {
					const item = cacheValue?.data?.find(({ id }) => id === resourceId);

					item.comment = comment;
				}
			});

			/* istanbul ignore next */
			if (!cacheResource) {
				return;
			}

			const item = {
				...cacheResource,
				comment,
			};

			cache.set(key, item);

			return item.comment;
		},

		putComment({ productId, movementId, comment, commentId }) {
			const url = `/products/movements/comments/${commentId}`;
			const method = 'PUT';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					payload: { comment },
				})
				.then(() =>
					this.updateCachedResource({
						productId,
						resourceId: movementId,
						data: {
							comment,
							commentId,
						},
					})
				);
		},

		postComment({ productId, movementId, comment }) {
			const url = `/products/${productId}/movements/${movementId}/comment`;
			const method = 'POST';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					payload: { comment },
				})
				.then(({ data }) =>
					this.updateCachedResource({
						productId,
						resourceId: movementId,
						data,
					})
				);
		},

		deleteComment({ productId, movementId, commentId }) {
			const url = `/products/movements/comments/${commentId}`;
			const method = 'DELETE';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(() =>
					this.updateCachedResource({
						productId,
						resourceId: movementId,
						data: {
							comment: '',
							commentId: '',
						},
					})
				);
		},
	},
});
